"use client";

import { useEffect, useState, Suspense } from "react";
import dynamic from "next/dynamic";
import type { Application } from "@splinetool/runtime";
import { ErrorBoundary } from "react-error-boundary";
import SparkleOrb from "./Sparkleorb";

// Dynamically import Spline with SSR disabled
const Spline = dynamic(() => import("@splinetool/react-spline"), {
  ssr: false,
});

// function LoadingFallback() {
//     return (
//         <div className="flex h-full w-full items-center justify-center">
//             <div className="h-12 w-12 animate-spin rounded-full border-b-2 border-t-2 border-white"></div>
//         </div>
//     )
// }

function ErrorFallback() {
  return (
    <div className="relative h-full w-full">
      <SparkleOrb color="#EE4137" />
      <div className="absolute inset-0 flex flex-col items-center justify-end pb-12 text-center text-white/30">
        <p className="text-sm font-medium tracking-widest uppercase">
          3D Asset Missing
        </p>
        <p className="mt-1 text-xs opacity-50">
          Falling back to canvas particles
        </p>
      </div>
    </div>
  );
}

export function ParticleSphere() {
  const [splineApp, setSplineApp] = useState<Application | null>(null);
  const [error, setError] = useState<boolean>(false);

  // Using a valid test URL as a temporary placeholder if the local file is missing
  // Replace with "/scene-f0f0f0.splinecode" once you've added the file to /public
  const sceneUrl = "/scene-f0f0f0.splinecode";

  const getZoomLevel = () => {
    if (typeof window === "undefined") return 1.4;
    const width = window.innerWidth;
    if (width < 768) return 0.9;
    if (width < 1024) return 1.0;
    return 1.4;
  };

  function onLoad(spline: Application) {
    setSplineApp(spline);
    const zoomLevel = getZoomLevel();
    spline.setZoom(zoomLevel);
  }

  function onError() {
    console.warn("Spline scene failed to load. Falling back to SparkleOrb.");
    setError(true);
  }

  useEffect(() => {
    const handleResize = () => {
      if (splineApp) {
        const zoomLevel = getZoomLevel();
        splineApp.setZoom(zoomLevel);
      }
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, [splineApp]);

  if (error) {
    return <ErrorFallback />;
  }

  return (
    <ErrorBoundary FallbackComponent={ErrorFallback}>
      <Suspense>
        <Spline
          scene={sceneUrl}
          onLoad={onLoad}
          onError={onError}
          style={{ width: "100%", height: "100%" }}
        />
      </Suspense>
    </ErrorBoundary>
  );
}
